import { useEffect, useState } from "react";
import { useSpark } from "@/lib/store";
import { getSharing, setCoachConnected, addSharedItem, removeSharedItem } from "@/lib/api";
import { ACTIVITIES } from "@/lib/activities";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { toast } from "sonner";

// Privacy by default. Nothing here is visible to a coach unless the user chooses it.
export const ShareWithCoach = () => {
  const { userId, profile } = useSpark();
  const [open, setOpen] = useState(false);
  const [sharing, setSharing] = useState(null);
  const [type, setType] = useState("compass");
  const [ref, setRef] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => { if (open) getSharing(userId).then(setSharing); }, [open, userId]);

  const threads = profile?.gold_threads_confirmed || [];
  const label = (item) => {
    if (item.type === "activity") return ACTIVITIES.find(a => a.key === item.ref)?.title || item.ref;
    if (item.type === "gold_thread") return threads.find(t => t.id === item.ref)?.theme || "A Gold Thread";
    if (item.type === "discussion") return item.note;
    return sharing?.types?.[item.type] || item.type;
  };

  const connect = async (connected) => {
    setSharing(s => ({ ...s, ...({}) }));
    const s = await setCoachConnected(userId, connected);
    setSharing(prev => ({ ...prev, ...s }));
    toast(connected ? "Connected. Only what you choose below is visible." : "Disconnected. Nothing is visible to your coach.");
  };

  const add = async () => {
    try {
      const s = await addSharedItem(userId, { type, ref, note });
      setSharing(prev => ({ ...prev, ...s }));
      setRef(""); setNote("");
      toast("Shared with your coach.");
    } catch (e) {
      toast(e?.response?.data?.detail || "Couldn't share that.");
    }
  };

  const remove = async (id) => {
    const s = await removeSharedItem(userId, id);
    setSharing(prev => ({ ...prev, ...s }));
  };

  const needsRef = type === "activity" || type === "gold_thread";

  return (
    <>
      <button data-testid="share-with-coach-btn" onClick={() => setOpen(true)} className="text-sm text-[#8A8A91] hover:text-[#17171C] underline-offset-4 hover:underline">
        Share with my coach
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent data-testid="share-sheet" className="w-full sm:max-w-md overflow-y-auto" style={{ background: "hsl(var(--spark-canvas))" }}>
          <SheetTitle className="text-2xl font-medium tracking-tight text-[#17171C]">Share with my coach</SheetTitle>
          <SheetDescription className="spark-voice text-lg text-[#56565D] mt-2">
            Your reflections belong to you. You decide what your coach sees.
          </SheetDescription>

          <div className="mt-8 rounded-2xl p-5" style={{ background: "hsl(var(--spark-cool))" }}>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-[#17171C] font-medium">Connect my Spark journey to my coach</p>
                <p className="text-xs text-[#56565D] mt-1">Off by default. Even when on, only the items you list below are shared.</p>
              </div>
              <button
                data-testid="coach-connect-toggle"
                role="switch"
                aria-checked={!!sharing?.coach_connected}
                onClick={() => connect(!sharing?.coach_connected)}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${sharing?.coach_connected ? "bg-[#2C50A0]" : "bg-[rgba(0,0,0,0.18)]"}`}
              >
                <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${sharing?.coach_connected ? "translate-x-5" : "translate-x-0.5"}`} />
              </button>
            </div>
          </div>

          <div className="mt-8">
            <p className="spark-eyebrow mb-3">Share</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(sharing?.types || {}).map(([k, v]) => (
                <button key={k} data-testid={`share-type-${k}`} onClick={() => { setType(k); setRef(""); }} className={`spark-chip ${type === k ? "active" : ""}`}>{v}</button>
              ))}
            </div>
            {needsRef && (
              <select data-testid="share-ref-select" value={ref} onChange={e => setRef(e.target.value)} className="mt-4 w-full rounded-xl border border-[rgba(0,0,0,0.12)] bg-white px-4 py-3 text-sm">
                <option value="">Choose…</option>
                {type === "activity" && ACTIVITIES.map(a => <option key={a.key} value={a.key}>{a.title}</option>)}
                {type === "gold_thread" && threads.map(t => <option key={t.id} value={t.id}>{t.theme}</option>)}
              </select>
            )}
            <textarea
              data-testid="share-note-input"
              rows={3}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder={type === "discussion" ? "What would you like to talk about?" : "Optional — anything you'd like your coach to know about this."}
              className="mt-4 w-full rounded-xl border border-[rgba(0,0,0,0.12)] bg-white px-4 py-3 text-sm leading-relaxed focus:outline-none focus:border-[#2C50A0]"
            />
            <button data-testid="share-add-btn" onClick={add} className="spark-btn-primary mt-3">Share this</button>
          </div>

          <div className="mt-10">
            <p className="spark-eyebrow mb-3">What your coach can see</p>
            {(sharing?.shared_items || []).length === 0 ? (
              <p data-testid="share-empty" className="text-sm text-[#8A8A91]">Nothing yet. Everything stays private until you share it.</p>
            ) : (
              <ul className="space-y-2">
                {sharing.shared_items.map(item => (
                  <li key={item.id} data-testid="shared-item" className="flex items-start justify-between gap-3 rounded-xl bg-white border border-[rgba(0,0,0,0.08)] px-4 py-3 text-sm">
                    <div>
                      <p className="text-[#17171C]">{label(item)}</p>
                      <p className="text-xs text-[#8A8A91] mt-0.5">{sharing.types?.[item.type]}{item.note && item.type !== "discussion" ? ` · ${item.note}` : ""}</p>
                    </div>
                    <button data-testid="shared-item-remove" onClick={() => remove(item.id)} className="text-xs text-[#8A8A91] hover:text-[#1F3D80]">Stop sharing</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default ShareWithCoach;
